import { Wallet, ShieldCheck, CreditCard, CheckCircle2 } from 'lucide-react';
import {
  MAX_CAMPAIGN_AMOUNT,
  MIN_CAMPAIGN_AMOUNT,
} from '@/features/campaigns/constants/campaignOptions';
import type { PreviewData } from '@/features/campaigns/types/campaign';
import { formatCount, formatMoney } from '@/shared/lib/format/money';
import { Row } from '../PreviewStepPrimitives';
import { resolvePreviewBill } from '../previewBilling';

type LaunchSummaryAsideProps = {
  data: PreviewData;
  availableBalance: number;
  exceedsMaxOrder?: boolean;
  belowMinOrder?: boolean;
  onBack?: () => void;
  sticky?: boolean;
  screenCount?: number;
  totalPlays?: number;
};

export function LaunchSummaryAside({
  data,
  availableBalance,
  exceedsMaxOrder = false,
  belowMinOrder = false,
  onBack,
  sticky = false,
  screenCount,
  totalPlays,
}: LaunchSummaryAsideProps) {
  const bill = resolvePreviewBill(data);
  const shortfall = Math.max(0, bill.payable - availableBalance);
  const balanceOk = shortfall <= 0;

  return (
    <aside
      className={`camp-section h-fit text-left !py-3 ${
        sticky ? 'lg:sticky lg:top-2' : ''
      }`}
    >
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-[var(--dash-brand)]" />
          <h2 className="m-0 text-[13px] font-semibold text-[var(--dash-ink)]">
            Order summary
          </h2>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-800">
          <ShieldCheck className="h-3 w-3" />
          Secure
        </span>
      </div>

      <div className="rounded-xl bg-gradient-to-br from-[var(--dash-brand)]/[0.09] to-[var(--dash-brand)]/[0.02] px-3.5 py-3 ring-1 ring-[var(--dash-brand)]/15">
        <p className="m-0 text-[10.5px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
          Amount due to launch
        </p>
        <p className="m-0 mt-0.5 text-[28px] leading-tight font-bold tracking-tight text-[var(--dash-ink)] tabular-nums">
          {formatMoney(bill.payable)}
        </p>
        <p className="m-0 mt-1 text-[11.5px] leading-relaxed text-[var(--dash-muted)]">
          Inclusive of taxes
          {bill.discount > 0 ? ' and after coupon discount' : ''}
          {screenCount != null && totalPlays != null
            ? ` · ${formatCount(totalPlays)} plays on ${screenCount} screen${
                screenCount === 1 ? '' : 's'
              }`
            : ''}
          .
        </p>
      </div>

      <p className="m-0 mt-3.5 text-[10.5px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
        Price breakdown
      </p>
      <div className="mt-1.5 space-y-1.5 text-[12.5px]">
        <Row label="Base subtotal" value={formatMoney(bill.subtotal)} />
        {bill.discount > 0 ? (
          <Row
            label="Coupon discount"
            value={`−${formatMoney(bill.discount)}`}
            tone="success"
          />
        ) : null}
        {bill.taxRows.length > 0 ? (
          bill.taxRows.map((row) => (
            <Row
              key={row.key}
              label={row.label}
              value={formatMoney(row.amount)}
            />
          ))
        ) : bill.totalTax > 0 ? (
          <Row label="GST" value={formatMoney(bill.totalTax)} />
        ) : null}
        <div className="border-t border-dashed border-[var(--dash-line)] pt-2 text-[13.5px] [&_span]:font-bold">
          <Row label="Total payable" value={formatMoney(bill.payable)} />
        </div>
      </div>

      <p className="m-0 mt-3.5 text-[10.5px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
        Payment
      </p>
      <div className="mt-1.5 space-y-1.5 text-[12.5px]">
        <Row
          label="Wallet balance"
          value={formatMoney(availableBalance)}
          tone={balanceOk ? 'success' : 'warn'}
        />
        <Row
          label={balanceOk ? 'Remaining after launch' : 'To pay now'}
          value={formatMoney(
            balanceOk ? availableBalance - bill.payable : shortfall,
          )}
          tone={balanceOk ? undefined : 'warn'}
        />
      </div>

      {belowMinOrder ? (
        <div className="mt-3 space-y-2">
          <p className="m-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium leading-relaxed text-red-800">
            Minimum order is {formatMoney(MIN_CAMPAIGN_AMOUNT)}. Increase slots
            to continue.
          </p>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex w-full items-center justify-center rounded-lg border border-[var(--dash-line)] bg-white px-3 py-2 text-sm font-semibold text-[var(--dash-ink)]"
            >
              Go back
            </button>
          ) : null}
        </div>
      ) : exceedsMaxOrder ? (
        <div className="mt-3 space-y-2">
          <p className="m-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium leading-relaxed text-red-800">
            Order exceeds {formatMoney(MAX_CAMPAIGN_AMOUNT)}. Reduce screens,
            dates, or slots.
          </p>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex w-full items-center justify-center rounded-lg border border-[var(--dash-line)] bg-white px-3 py-2 text-sm font-semibold text-[var(--dash-ink)]"
            >
              Go back
            </button>
          ) : null}
        </div>
      ) : shortfall > 0 ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50/50 px-2.5 py-2 text-xs">
          <CreditCard className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-700" />
          <div>
            <p className="m-0 font-semibold text-sky-950">
              Short by {formatMoney(shortfall)}
            </p>
            <p className="m-0 mt-0.5 text-[11px] leading-relaxed text-sky-900/85">
              Wallet {formatMoney(availableBalance)} · due{' '}
              {formatMoney(bill.payable)}. Pay &amp; Launch tops up and launches
              in one UPI / card / net-banking payment.
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-2.5 py-2 text-xs font-semibold text-emerald-900">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
          <span>Wallet covers this order — ready to launch</span>
        </div>
      )}
    </aside>
  );
}
